import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import Panel from 'components/Panel';
import Break from 'components/Break';
import { BlueBorderButton, BlueButton } from 'components/Button';
import SelectableSearchInput from 'components/SelectableSearchInput';
import {
  SubPanel,
  SubPanelRow,
  PanelRowDescriptor,
  PanelRowDisplay,
  PanelRowTextForm,
  PanelButtonRow,
  StyledForm,
  PanelContent,
  FullPanel,
  RadioButtons,
  RadioOption,
  RadioInput,
  MultipleInputRowContainer,
  InputAndLabelContainer,
  LabledInput,
  InputUnitLabel
} from 'components/PanelComponents';
import Text from 'components/Text';

import { createDevice } from 'reducers/device/actions';
import {
  NO_PARENT_OPTION_VALUE,
  SIDEPANEL_STATES,
  RESOURCE_TYPES,
  LOCAL_RESOURCE_MAPPED_VALUES
} from 'utils/appConstants';
import {
  setSidePanel,
  closeSidePanel,
  revertSidePanel
} from '../../reducers/sidePanel/actions';
import { determineBack } from 'utils/determineBack';
import { fetchAllTopoNodes } from 'api/toponode';
import { fetchAllIndeNodes } from 'api/indenode';
import { fetchAllAggregators } from 'api/aggregator';
import { convertToSeconds } from 'utils/format';
import { POLL_DEVICES, subscribe, unsubscribe } from 'reducers/polling/actions';
import { duplicateDevice } from 'reducers/device/actions';

const deviceConfigFlag =
  window.env.REACT_APP_DEVICE_PROTOCOL_FLAG_ENABLED === 'true';

const NewDevice = ({ isActive }) => {
  const dispatch = useDispatch();
  const sidePanel = useSelector(state => state.sidePanel);
  const data = useSelector(state => state.sidePanel.data);
  const deviceIds = useSelector(state => state.device.devices);
  const [deviceId, setDeviceId] = useState('');
  const [lfdi, setLfdi] = useState('');
  const [registrationPin, setRegistrationPin] = useState('');
  const [protocol, setProtocol] = useState(0);
  const [aggregatorId, setAggregatorId] = useState('');
  const [topoNodeIdList, setTopoNodeIdList] = useState([]);
  const [indeNodeIdList, setIndeNodeIdList] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [parentInfo, setParentInfo] = useState({
    parentTopoNodeId: null,
    parentAggregatorId: null,
    parentIndeNodeId: null
  });
  const [isLoading, setIsLoading] = useState(true);
  const [topoNodeIds, setTopoNodeIds] = useState([]);
  const [indeNodeIds, setIndeNodeIds] = useState([]);
  const [aggregatorIds, setAggregatorIds] = useState([]);
  const [derInfoPostRateHours, setDerInfoPostRateHours] = useState(null);
  const [derInfoPostRateMinutes, setDerInfoPostRateMinutes] = useState(null);
  const [derInfoPostRateSeconds, setDerInfoPostRateSeconds] = useState(null);
  const [meterDataPostRateHours, setMeterDataPostRateHours] = useState(null);
  const [meterDataPostRateMinutes, setMeterDataPostRateMinutes] =
    useState(null);
  const [meterDataPostRateSeconds, setMeterDataPostRateSeconds] =
    useState(null);
  const [derInfoPostRateIsError, setDerInfoPostRateIsError] = useState(false);
  const [meterDataPostRateIsError, setMeterDataPostRateIsError] =
    useState(false);
  const [deviceIdIsError, setDeviceIdIsError] = useState(false);
  const [lfdiIsError, setLdfiIsError] = useState(false);
  const [registrationPinIsError, setRegistrationPinIsError] = useState(false);

  const SUBSCRIBER_NAME = 'NewDevice';
  const pollRate = parseInt(window.env.REACT_APP_GLOBAL_POLL_RATE_FREQUENCY);

  useEffect(() => {
    if (isActive) {
      dispatch(
        subscribe({
          subscriber: SUBSCRIBER_NAME,
          pollTypes: [
            {
              type: POLL_DEVICES
            }
          ],
          pollRate
        })
      );
    } else {
      dispatch(unsubscribe(SUBSCRIBER_NAME));
    }
    return () => {
      if (isActive) {
        dispatch(unsubscribe(SUBSCRIBER_NAME));
      }
    };
  }, [dispatch, isActive, pollRate]);

  useEffect(() => {
    //Check if we are adding this as a child, we will have passed in a parent ID
    //We will use this object later to disallowed removing this id from the device's properties.
    let aggregatorIds = [];
    let indeNodeIds = [];
    let topoNodeIds = [];
    let parentInfo = {
      parentTopoNodeId: null,
      parentIndeNodeId: null,
      parentAggregatorId: null
    };
    if (data) {
      parentInfo.parentTopoNodeId = data.parentTopoNodeId;
      parentInfo.parentIndeNodeId = data.parentIndeNodeId;
      parentInfo.parentAggregatorId = data.parentAggregatorId;
    }
    if (!data || !data.parentTopoNodeId) {
      const fetchTopoNodes = async () => {
        const topoNodes = await fetchAllTopoNodes();
        topoNodeIds = topoNodes
          .filter(
            topoNode =>
              topoNode.localNode !== LOCAL_RESOURCE_MAPPED_VALUES.REMOTE
          )
          .map(topoNode => topoNode.topoNodeId);
        setTopoNodeIds(topoNodeIds);
      };
      fetchTopoNodes();
    }
    if (!data || !data.parentIndeNodeId) {
      const fetchIndeNodes = async () => {
        const indeNodes = await fetchAllIndeNodes();
        indeNodeIds = indeNodes
          .filter(
            indeNode =>
              indeNode.localNode !== LOCAL_RESOURCE_MAPPED_VALUES.REMOTE
          )
          .map(indeNode => indeNode.indeNodeId);
        setIndeNodeIds(indeNodeIds);
      };
      fetchIndeNodes();
    }
    if (!data || !data.parentAggregatorId) {
      const fetchAggregator = async () => {
        const aggregators = await fetchAllAggregators();
        aggregatorIds = aggregators.map(aggregator => aggregator.aggregatorId);
        setAggregatorIds(aggregatorIds);
      };
      fetchAggregator();
    }

    setParentInfo(parentInfo);

    setIsLoading(false);
  }, []);

  const handleInputChange = event => {
    const target = event.target;
    const value = target.value === NO_PARENT_OPTION_VALUE ? null : target.value;
    const name = target.name;

    if (name === 'deviceId') {
      setDeviceId(value);
    } else if (name === 'lfdi') {
      setLfdi(value);
    } else if (name === 'registrationPin') {
      setRegistrationPin(value);
    } else if (name === 'derInfoPostRateHours') {
      setDerInfoPostRateHours(value);
    } else if (name === 'derInfoPostRateMinutes') {
      setDerInfoPostRateMinutes(value);
    } else if (name === 'derInfoPostRateSeconds') {
      setDerInfoPostRateSeconds(value);
    } else if (name === 'meterDataPostRateHours') {
      setMeterDataPostRateHours(value);
    } else if (name === 'meterDataPostRateMinutes') {
      setMeterDataPostRateMinutes(value);
    } else if (name === 'meterDataPostRateSeconds') {
      setMeterDataPostRateSeconds(value);
    } else if (name === 'protocol') {
      setProtocol(value);
    }
  };

  const handleSearchInputChange = (selectedOptions, input) => {
    if (input === 'topoNodeIdList') {
      const newTopoNodeValue = selectedOptions ? [selectedOptions.value] : [];
      setTopoNodeIdList(newTopoNodeValue);
    } else if (input === 'aggregatorId') {
      const newAggregatorValue = selectedOptions ? selectedOptions.value : '';
      setAggregatorId(newAggregatorValue);
    } else if (input === 'indeNodeIdList') {
      const newIndeNodeValue = selectedOptions
        ? selectedOptions.map(option => option.value)
        : [];
      setIndeNodeIdList(newIndeNodeValue);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setIsSubmitting(true);

    const alreadyExists = deviceIds.find(
      element => element.deviceId === deviceId
    );

    if (alreadyExists) {
      setIsSubmitting(false);
      return dispatch(duplicateDevice(deviceId));
    }

    const parsedAsIntRegistrationPin = parseInt(registrationPin);
    const parsedAsIntDerInfoPostRateHours = parseInt(derInfoPostRateHours);
    const parsedAsIntDerInfoPostRateMinutes = parseInt(derInfoPostRateMinutes);
    const parsedAsIntDerInfoPostRateSeconds = parseInt(derInfoPostRateSeconds);
    const parsedAsIntMeterDataPostRateHours = parseInt(meterDataPostRateHours);
    const parsedAsIntMeterDataPostRateMinutes = parseInt(
      meterDataPostRateMinutes
    );
    const parsedAsIntMeterDataPostRateSeconds = parseInt(
      meterDataPostRateSeconds
    );
    const parsedAsIntProtocol = parseInt(protocol);

    // TODO: Current validation is here, will need to be polished.
    let passed = true;
    if (
      !deviceId ||
      !/^[a-zA-Z0-9_.~-]*$/.test(deviceId) ||
      deviceId.length > 64
    ) {
      setDeviceIdIsError(true);
      passed = false;
    } else {
      setDeviceIdIsError(false);
    }

    if (!/^[a-zA-Z0-9]*$/gi.test(lfdi) || lfdi.length !== 40) {
      setLdfiIsError(true);
      passed = false;
    } else {
      setLdfiIsError(false);
    }

    if (
      registrationPin &&
      (!/^\d+$/.test(registrationPin) ||
        isNaN(parsedAsIntRegistrationPin) ||
        parsedAsIntRegistrationPin > 999999 ||
        parsedAsIntRegistrationPin < 0)
    ) {
      setRegistrationPinIsError(true);
      passed = false;
    } else {
      setRegistrationPinIsError(false);
    }

    const convertedDerInfoPostRate = convertToSeconds({
      hours: parsedAsIntDerInfoPostRateHours,
      minutes: parsedAsIntDerInfoPostRateMinutes,
      seconds: parsedAsIntDerInfoPostRateSeconds
    });

    const convertedMeterDataPostRate = convertToSeconds({
      hours: parsedAsIntMeterDataPostRateHours,
      minutes: parsedAsIntMeterDataPostRateMinutes,
      seconds: parsedAsIntMeterDataPostRateSeconds
    });

    if (
      derInfoPostRateHours ||
      derInfoPostRateSeconds ||
      derInfoPostRateMinutes
    ) {
      if (
        isNaN(convertedDerInfoPostRate) ||
        convertedDerInfoPostRate > 4294967295 ||
        convertedDerInfoPostRate < 0
      ) {
        setDerInfoPostRateIsError(true);
        passed = false;
      } else {
        setDerInfoPostRateIsError(false);
      }
    }

    if (
      meterDataPostRateHours ||
      meterDataPostRateSeconds ||
      meterDataPostRateMinutes
    ) {
      if (
        isNaN(convertedMeterDataPostRate) ||
        convertedMeterDataPostRate > 4294967295 ||
        convertedMeterDataPostRate < 0
      ) {
        setMeterDataPostRateIsError(true);
        passed = false;
      } else {
        setMeterDataPostRateIsError(false);
      }
    }

    if (!passed) {
      return;
    }

    const requestBody = {
      deviceId,
      lfdi,
      protocol: parsedAsIntProtocol,
      indeNodeIdList: indeNodeIdList,
      topoNodeIdList: topoNodeIdList
    };

    if (
      parentInfo.parentTopoNodeId !== null &&
      parentInfo.parentTopoNodeId !== undefined
    ) {
      requestBody.topoNodeIdList = [parentInfo.parentTopoNodeId];
    }
    if (parentInfo.parentAggregatorId || aggregatorId) {
      requestBody.aggregatorId = parentInfo.parentAggregatorId || aggregatorId;
    }
    if (
      parentInfo.parentIndeNodeId !== null &&
      parentInfo.parentIndeNodeId !== undefined
    ) {
      requestBody.indeNodeIdList = [parentInfo.parentIndeNodeId];
    }
    if (!isNaN(parsedAsIntRegistrationPin)) {
      requestBody.registrationPin = parsedAsIntRegistrationPin;
    }
    if (Number.isInteger(convertedDerInfoPostRate)) {
      requestBody.edevPostRate = convertedDerInfoPostRate;
    }
    if (Number.isInteger(convertedMeterDataPostRate)) {
      requestBody.mupPostRate = convertedMeterDataPostRate;
    }

    try {
      const response = await dispatch(createDevice(requestBody));
      dispatch(
        setSidePanel({
          entity: RESOURCE_TYPES.DEVICE,
          state: SIDEPANEL_STATES.READ,
          sfdi: response.sfdi
        })
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isActive || (isActive && isLoading)) {
    return null;
  } else {
    return (
      <StyledForm onSubmit={handleSubmit}>
        <FullPanel>
          <Panel title="New Device" primaryHeader />
          <PanelContent>
            <SubPanel>
              <SubPanelRow>
                <PanelRowDescriptor>ID</PanelRowDescriptor>
                <PanelRowTextForm
                  name="deviceId"
                  onChange={handleInputChange}
                  isError={deviceIdIsError}
                  type="text"
                  minLength="1"
                  maxLength="64"
                  pattern="[a-zA-Z0-9_.~-]*$"
                  required
                />
              </SubPanelRow>

              <SubPanelRow>
                <PanelRowDescriptor>LFDI</PanelRowDescriptor>
                <PanelRowTextForm
                  name="lfdi"
                  onChange={handleInputChange}
                  isError={lfdiIsError}
                  type="text"
                  required
                  minLength="40"
                  maxLength="40"
                  pattern="[0-9a-zA-Z]+"
                />
              </SubPanelRow>

              <SubPanelRow>
                <PanelRowDescriptor>
                  <Text fontSize="12px">Registration Pin </Text>
                  <Text fontSize="12px">(Optional)</Text>
                </PanelRowDescriptor>
                <PanelRowTextForm
                  name="registrationPin"
                  onChange={handleInputChange}
                  isError={registrationPinIsError}
                  type="number"
                  min="0"
                  max="999999"
                />
              </SubPanelRow>

              <SubPanelRow>
                <PanelRowDescriptor>
                  <Text fontSize="12px">Requested DER Info Post Rate</Text>
                  <Text fontSize="12px">(Optional)</Text>
                </PanelRowDescriptor>

                {/* hours */}
                <MultipleInputRowContainer>
                  <InputAndLabelContainer width="30%">
                    <LabledInput
                      type="number"
                      name="derInfoPostRateHours"
                      value={derInfoPostRateHours}
                      onChange={handleInputChange}
                      min="0"
                      max="11929"
                      isError={derInfoPostRateIsError}
                    />
                    <InputUnitLabel>hrs</InputUnitLabel>
                  </InputAndLabelContainer>
                  {/* minutes */}
                  <InputAndLabelContainer width="30%">
                    <LabledInput
                      type="number"
                      name="derInfoPostRateMinutes"
                      value={derInfoPostRateMinutes}
                      onChange={handleInputChange}
                      min="0"
                      max="60"
                      isError={derInfoPostRateIsError}
                    />
                    <InputUnitLabel>min</InputUnitLabel>
                  </InputAndLabelContainer>
                  {/* seconds */}
                  <InputAndLabelContainer width="30%">
                    <LabledInput
                      type="number"
                      name="derInfoPostRateSeconds"
                      value={derInfoPostRateSeconds}
                      onChange={handleInputChange}
                      min="0"
                      max="60"
                      isError={derInfoPostRateIsError}
                    />
                    <InputUnitLabel>sec</InputUnitLabel>
                  </InputAndLabelContainer>
                </MultipleInputRowContainer>
              </SubPanelRow>

              <SubPanelRow>
                <PanelRowDescriptor>
                  <Text fontSize="12px">Requested Meter Data Post Rate</Text>
                  <Text fontSize="12px">(Optional)</Text>
                </PanelRowDescriptor>

                {/* hours */}
                <MultipleInputRowContainer>
                  <InputAndLabelContainer width="30%">
                    <LabledInput
                      type="number"
                      name="meterDataPostRateHours"
                      value={meterDataPostRateHours}
                      onChange={handleInputChange}
                      min="0"
                      max="11929"
                      isError={meterDataPostRateIsError}
                    />
                    <InputUnitLabel>hrs</InputUnitLabel>
                  </InputAndLabelContainer>
                  {/* minutes */}
                  <InputAndLabelContainer width="30%">
                    <LabledInput
                      type="number"
                      name="meterDataPostRateMinutes"
                      value={meterDataPostRateMinutes}
                      onChange={handleInputChange}
                      min="0"
                      max="60"
                      isError={meterDataPostRateIsError}
                    />
                    <InputUnitLabel>min</InputUnitLabel>
                  </InputAndLabelContainer>
                  {/* seconds */}
                  <InputAndLabelContainer width="30%">
                    <LabledInput
                      type="number"
                      name="meterDataPostRateSeconds"
                      value={meterDataPostRateSeconds}
                      onChange={handleInputChange}
                      min="0"
                      max="60"
                      isError={meterDataPostRateIsError}
                    />
                    <InputUnitLabel>sec</InputUnitLabel>
                  </InputAndLabelContainer>
                </MultipleInputRowContainer>
              </SubPanelRow>

              {deviceConfigFlag && (
                <SubPanelRow>
                  <PanelRowDescriptor>
                    <Text fontSize="12px">Protocol</Text>
                    <Text fontSize="12px">(Optional)</Text>
                  </PanelRowDescriptor>
                  <RadioButtons>
                    <RadioOption>
                      <label>
                        <RadioInput
                          name="protocol"
                          onChange={handleInputChange}
                          type="radio"
                          value={0}
                          checked={parseInt(protocol) === 0}
                        />
                        2030.5
                      </label>
                    </RadioOption>
                    <RadioOption>
                      <label>
                        <RadioInput
                          name="protocol"
                          onChange={handleInputChange}
                          type="radio"
                          value={1}
                          checked={parseInt(protocol) === 1}
                        />
                        Crosslink
                      </label>
                    </RadioOption>
                  </RadioButtons>
                </SubPanelRow>
              )}
            </SubPanel>
            <Break margin="16px 0" />

            <SubPanel>
              <SubPanelRow>
                <Text fontSize="12px" fontWeight="600">
                  Edit Associations (Optional)
                </Text>
              </SubPanelRow>
              {!parentInfo.parentTopoNodeId && (
                <SubPanelRow>
                  <PanelRowDescriptor>Parent Topology Node</PanelRowDescriptor>
                  <PanelRowDisplay>
                    <SelectableSearchInput
                      options={topoNodeIds.filter(
                        id =>
                          topoNodeIdList.indexOf(id) < 0 &&
                          id !== parentInfo.parentTopoNodeId
                      )}
                      onChange={selected =>
                        handleSearchInputChange(selected, 'topoNodeIdList')
                      }
                      selected={topoNodeIdList}
                      name="toponode-select"
                    />
                  </PanelRowDisplay>
                </SubPanelRow>
              )}

              {!parentInfo.parentAggregatorId && (
                <SubPanelRow>
                  <PanelRowDescriptor>Aggregator ID</PanelRowDescriptor>
                  <PanelRowDisplay>
                    <SelectableSearchInput
                      isMulti={false}
                      options={aggregatorIds}
                      onChange={selected =>
                        handleSearchInputChange(selected, 'aggregatorId')
                      }
                      selected={aggregatorId}
                      name="aggregator-select"
                    />
                  </PanelRowDisplay>
                </SubPanelRow>
              )}
              {!parentInfo.parentIndeNodeId && (
                <SubPanelRow>
                  <PanelRowDescriptor>Independent Nodes</PanelRowDescriptor>
                  <PanelRowDisplay>
                    <SelectableSearchInput
                      isMulti={true}
                      options={indeNodeIds.filter(
                        id =>
                          indeNodeIdList.indexOf(id) < 0 &&
                          id !== parentInfo.parentIndeNodeId
                      )}
                      onChange={selected =>
                        handleSearchInputChange(selected, 'indeNodeIdList')
                      }
                      selected={indeNodeIdList}
                      name="indenode-select"
                    />
                  </PanelRowDisplay>
                </SubPanelRow>
              )}
            </SubPanel>
          </PanelContent>
          <PanelButtonRow>
            <BlueBorderButton
              type="button"
              onClick={() =>
                determineBack(
                  sidePanel,
                  () => dispatch(revertSidePanel()),
                  () => dispatch(closeSidePanel())
                )
              }
            >
              Cancel
            </BlueBorderButton>
            <BlueButton type="submit" disabled={isSubmitting}>
              Create Device
            </BlueButton>
          </PanelButtonRow>
        </FullPanel>
      </StyledForm>
    );
  }
};

export default NewDevice;
